// can't import these because the iframe script is having issues

// import { easeInOutQuad, getScrollRoot } from './../core/utils';

/**
 * Get the root scrolling element x-browser
 *
 * @export
 * @returns {DOMElement}
 */
function getScrollRoot() {
  return document.scrollingElement || document.documentElement || document.body;
}

/**
 * Es lint shouting about assigning to function params below lines disabled.
 *
 * @param {Number} t - current time
 * @param {Number} s - start position
 * @param {Number} c - change in position
 * @param {Number} d - duration of change
 */
function easeInOutQuad(time, startPos, posChange, duration) {
  let newTime = time / (duration / 2);

  if (newTime < 1) {
    return posChange / 2 * newTime * newTime + startPos;
  }
  newTime -= 1;
  return -posChange / 2 * (newTime * (newTime - 2) - 1) + startPos;
}

/**
 * A make a cancellable scroll animation
 *
 * @export
 * @class ScrollTo
 */
export default class ScrollTo {
  /**
   * Creates an instance of ScrollTo.
   * @param {*} options
   * @param {HTMLElement} options.target the element to scroll to
   * @param {Number} options.duration the total length of time to animate
   * @param {Number} options.targetY the y target to scroll to, ignored if el is set
   *
   * @memberof ScrollTo
   */
  constructor() {
    this.scrollRoot = getScrollRoot();
    this.easingFunc = easeInOutQuad;
    this.animateScroll = this.animateScroll.bind(this);

    /**
     * Target element to scroll to
     *
     * @memberof ScrollTo
     */
    this.targetElement = null;

    /**
     * Target y value to scroll to
     */
    this.targetY = 0;

    /**
     * Duration of animation
     *
     * @memberof ScrollTo
     */
    this.duration = 400;

    /**
     * Controls how many updates happen. smaller numbers cause more updates
     *
     * @memberof ScrollTo
     */
    this.timing = 10;

    /**
     * Time of last update
     *
     * @memberof ScrollTo
     */
    this.lastUpdate = Date.now();

    /**
     * The last frame animation for canceling the animation
     *
     * @memberof ScrollTo
     */
    this.lastFrame = null;

    /**
     * Initial scroll root pos
     *
     * @memberof ScrollTo
     */
    this.startPos = 0;

    /**
     * The position we're trying to get to
     *
     * @memberof ScrollTo
     */
    this.toPos = 0;

    /**
     * How far we need to travel
     *
     * @memberof ScrollTo
     */
    this.totalDistance = 0;

    /**
     * Callback for the end of scroll
     *
     * @type {Function}
     * @memberof ScrollTo
     */
    this.callback = null;
  }

  /**
   * Get extend options with defaults
   *
   * @param {*} options same as constructor
   * @returns {*}
   * @memberof ScrollTo
   */
  getSettings(options) {
    return Object.assign(
      {},
      {
        duration: this.duration,
        lastFrame: this.lastFrame
      },
      options
    );
  }

  /**
   * Reset
   *
   * @param {*} options
   * @memberof ScrollTo
   */
  reset(options) {
    ({
      target: this.targetElement,
      duration: this.duration,
      callback: this.callback,
      targetY: this.targetY,
    } = this.getSettings(options));

    if (!this.targetElement && !this.targetY) {
      throw new Error('No target to scroll to');
    }

    if (!this.duration || this.duration <= 0 || Number.isNaN(this.duration / 1)) {
      throw new Error('Need positive duration.');
    }

    this.startPos = this.scrollRoot.scrollTop;

    let top = this.targetY;
    if (this.targetElement) {
      ({ top } = this.targetElement.getBoundingClientRect());
    }

    this.toPos = top + this.startPos; // doesn't work under...

    this.totalDistance = this.toPos - this.startPos;

    this.currentTime = 0;
    this.lastUpdate = Date.now();
  }

  /**
   * Clear animation
   *
   * @memberof ScrollTo
   */
  clear() {
    if (this.lastFrame) {
      window.cancelAnimationFrame(this.lastFrame);
    }
  }

  /**
   * Stop the animation
   *
   * @memberof ScrollTo
   */
  stop() {
    this.clear();
  }

  /**
   * Do the scroll
   *
   * @param {*} options same as constructor
   * @memberof ScrollTo
   */
  scroll(options) {
    this.reset(options);

    this.animateScroll();
  }

  /**
   * Frame draw function
   *
   * @memberof ScrollTo
   */
  animateScroll() {
    const time = Date.now();
    const increment = time - this.lastUpdate;
    this.lastUpdate = time;

    this.currentTime += increment;

    const pos = this.easingFunc(
      this.currentTime,
      this.startPos,
      this.totalDistance,
      this.duration
    );

    // update pos
    this.scrollRoot.scrollTop = pos;

    if (this.currentTime < this.duration) {
      this.lastFrame = requestAnimationFrame(this.animateScroll);
      return;
    }

    if (this.currentFrame === this.duration) {
      this.scrollRoot.scrollTop = this.toPos;
    }

    this.animateCallback();
  }

  /**
   * Do the callback if set
   *
   * @memberof ScrollTo
   */
  animateCallback() {
    if (this.callback && typeof this.callback === 'function') {
      this.callback();
    }
  }
}
